import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, CheckSquare, Calendar, User } from 'lucide-react';
import { formatForDisplay, getRelativeTime, isOverdue } from '../../../utils/timezoneUtils';
import { useAuth } from '../../../context/AuthContext';
import AssigneeDropdown from './AssigneeDropdown';
import TaskModal from './TaskModal';


const TaskItem = ({ task, onUpdateTask, onDeleteTask, onEditTask }) => {
  const [showActions, setShowActions] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const { currentUser } = useAuth();

  // Check if current user is admin (handle both role ID arrays and role objects)
  const isAdmin = currentUser?.roles && Array.isArray(currentUser.roles) &&
    (currentUser.roles.includes(1) || currentUser.roles.some(role => role.id === 1));
  
  const handleToggleComplete = () => {
    onUpdateTask(task.id, { is_completed: !task.is_completed });
  };

  const handleDelete = () => {
    if (window.confirm(`Delete task "${task.title}"?`)) {
      onDeleteTask(task.id);
    }
  };

  const handleAssign = async (taskId, userId, userName) => {
    setIsAssigning(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/project-tracker/tasks/${taskId}/assign`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ assigned_to: userId })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to assign task');
      }
    } catch (error) {
      console.error('Error assigning task:', error);
      throw error;
    } finally {
      setIsAssigning(false);
    }
  };

  const isTaskOverdue = task.end_datetime && isOverdue(task.end_datetime) && !task.is_completed;

  return (
    <div 
      className={`group p-2 rounded border transition-all duration-200 ${
        task.is_completed
          ? 'bg-slate-800/50 border-slate-700/50'
          : isTaskOverdue
          ? 'bg-red-900/10 border-red-700/30'
          : 'bg-slate-800 border-slate-700 hover:border-slate-600'
      }`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      <div className="flex items-center space-x-2">
        <button
          onClick={handleToggleComplete}
          className="flex-shrink-0"
        >
          <CheckSquare 
            className={`w-4 h-4 transition-colors ${
              task.is_completed 
                ? 'text-green-400 fill-current' 
                : 'text-slate-400 hover:text-green-400'
            }`} 
          />
        </button>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <div className="space-y-1">
                <h4 className={`font-medium text-sm leading-tight ${
                  task.is_completed
                    ? 'line-through text-slate-500'
                    : isTaskOverdue
                    ? 'text-red-200'
                    : 'text-slate-200'
                }`}>
                  {task.title}
                </h4>
                {task.description && (
                  <p className={`text-xs leading-relaxed ${
                    task.is_completed ? 'text-slate-600' : 'text-slate-500'
                  }`}>
                    {task.description}
                  </p>
                )}
              </div>
              
              {/* Compact metadata row */}
              <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-slate-500">
                {task.created_by_name && (
                  <span className="flex items-center">
                    <User className="w-3 h-3 mr-1" />
                    {task.created_by_name}
                  </span>
                )}
                {task.end_datetime && (
                  <span className={`flex items-center ${isTaskOverdue ? 'text-red-400 font-medium' : ''}`}>
                    <Calendar className="w-3 h-3 mr-1" />
                    {getRelativeTime(task.end_datetime)}
                  </span>
                )}
                {!!task.is_completed && task.completed_by_name && (
                  <span className="flex items-center text-green-400">
                    <CheckSquare className="w-3 h-3 mr-1" />
                    ✓ {task.completed_by_name}
                  </span>
                )}
                {/* Assignment display */}
                <AssigneeDropdown
                  taskId={task.id}
                  currentAssigneeId={task.assigned_to}
                  currentAssigneeName={task.assigned_to_full_name || task.assigned_to_name}
                  onAssign={handleAssign}
                  isUpdating={isAssigning}
                  size="sm"
                />
              </div>
            </div>
            
            <div className={`flex items-center space-x-1 transition-opacity ${
              showActions ? 'opacity-100' : 'opacity-0'
            }`}>
              <button
                onClick={() => onEditTask(task)}
                className="p-1 text-slate-400 hover:text-blue-400 hover:bg-slate-700 rounded transition-colors"
                title="Edit task"
              >
                <Edit className="w-3 h-3" />
              </button>
              <button
                onClick={handleDelete}
                className="p-1 text-slate-400 hover:text-red-400 hover:bg-slate-700 rounded transition-colors"
                title="Delete task"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

const SimpleTaskList = ({
  projectId,
  tasks = [],
  onCreateTask,
  onUpdateTask,
  onDeleteTask,
  onUpdateProject,
  selectedProject,
  isSubmitting
}) => {
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);

  const handleCreateTask = async (formData) => {
    await onCreateTask(formData);
    setShowTaskModal(false);
  };

  const handleUpdateTask = async (formData) => {
    await onUpdateTask(editingTask.id, formData);
    setEditingTask(null);
    setShowTaskModal(false);
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setShowTaskModal(true);
  };

  const handleCloseModal = () => {
    setShowTaskModal(false);
    setEditingTask(null);
  };


  // Ensure tasks is always an array
  const safeTasksArray = Array.isArray(tasks) ? tasks : [];
  const completedTasks = safeTasksArray.filter(task => task && task.is_completed).length;
  const totalTasks = safeTasksArray.length;
  
  // Check if all tasks are completed and project is not already completed
  const allTasksCompleted = totalTasks > 0 && completedTasks === totalTasks;
  const projectNotCompleted = selectedProject?.status !== 'completed';
  const showMarkCompleteButton = allTasksCompleted && projectNotCompleted;
  
  const handleMarkComplete = async () => {
    if (onUpdateProject && selectedProject) {
      try {
        await onUpdateProject(selectedProject.id, {
          name: selectedProject.name,
          description: selectedProject.description || '',
          status: 'completed'
        });
      } catch (error) {
        console.error('Failed to mark project as complete:', error);
      }
    }
  };

  return (
    <div className="space-y-4">
      {/* Task Stats / Mark Complete */}
      {totalTasks > 0 && (
        <div className="bg-slate-700/50 rounded-lg p-4">
          {showMarkCompleteButton ? (
            /* Mark as Complete Button */
            <button
              onClick={handleMarkComplete}
              disabled={isSubmitting}
              className="w-full bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white p-3 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2"
            >
              <span>🎉</span>
              <span>All tasks complete! Mark project as Complete?</span>
            </button>
          ) : selectedProject?.status === 'completed' ? (
            /* Project Already Completed */
            <div className="bg-green-100 border border-green-400 text-green-700 p-3 rounded-lg flex items-center justify-center space-x-2">
              <span>✅</span>
              <span className="font-medium">Project completed</span>
            </div>
          ) : (
            /* Regular Progress Bar */
            <>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-300">Progress</span>
                <span className="text-sm font-medium text-slate-300">
                  {completedTasks}/{totalTasks} tasks
                </span>
              </div>
              <div className="w-full bg-slate-600 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0}%` }}
                />
              </div>
            </>
          )}
        </div>
      )}

      {/* Add Task Button */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white">Tasks</h3>
        <button
          onClick={() => setShowTaskModal(true)}
          className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg transition-colors text-sm"
        >
          <Plus className="w-4 h-4" />
          <span>Add Task</span>
        </button>
      </div>

      {/* Task Modal */}
      <TaskModal
        isOpen={showTaskModal}
        onClose={handleCloseModal}
        onSubmit={editingTask ? handleUpdateTask : handleCreateTask}
        isSubmitting={isSubmitting}
        initialTask={editingTask}
        projectId={projectId}
      />

      {/* Tasks List */}
      {totalTasks === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-slate-700 rounded-lg">
          <div className="w-12 h-12 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-3">
            <CheckSquare className="w-6 h-6 text-slate-400" />
          </div>
          <h3 className="text-lg font-medium text-slate-200 mb-2">No tasks yet</h3>
          <p className="text-slate-400 mb-4">Get started by adding your first task to this project.</p>
          <button
            onClick={() => setShowTaskModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            Add First Task
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {safeTasksArray.map((task) => (
            <TaskItem
              key={task.id}
              task={task}
              onUpdateTask={onUpdateTask}
              onDeleteTask={onDeleteTask}
              onEditTask={handleEditTask}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SimpleTaskList;